module.exports={render:function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _vm._m(0)
},staticRenderFns: [function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _c('div', {
    staticClass: "chapter"
  }, [_c('div', {
    staticClass: "inner_page"
  }, [_c('p', {
    staticClass: "sp",
    attrs: {
      "id": "pref"
    }
  }), _vm._v(" "), _c('p', {
    staticClass: "FMH"
  }, [_c('span', {
    staticClass: "ePub-SC"
  }, [_c('a', {
    attrs: {
      "id": "pg_ix"
    }
  }, [_vm._v("PREFACE")])])]), _c('a', {
    attrs: {
      "id": "pg_ix"
    }
  }, [_c('p', {
    staticClass: "EP"
  }, [_vm._v("Time flaps on the mast. There we stop; there we stand. Rigid, the skeleton of habit alone upholds the human frame.")]), _vm._v(" "), _c('p', {
    staticClass: "EPC"
  }, [_vm._v("Virginia Woolf, 1925/1996, 48")]), _vm._v(" "), _c('p', {
    staticClass: "TNI"
  }, [_vm._v("This book supports a phenomenological approach to understanding human experiences. This includes the importance of narrative, of telling stories, as the backbone of human culture and individual lives, and the competing desires that enchant the world that our body encounters, beckoning us forever onward. The book is not simply about neuropsychiatry as a medical discipline, but it is in many ways much more a reflection on the way the brain and its functions have been viewed over the centuries, as well as on the huge change in orientation, germinating within romanticism, which has given us an understanding of our dynamic, active, creative brain. It is about the dawning and development of artistic creativity and the conflicts between different philosophical views of our world and our position in it. Congruence not disharmony, connectivity not separation, uniting not splitting, wholeness not hollowness, the self in the world not alienated from it—all of these are embedded in the romantic view.")]), _vm._v(" "), _c('p', {
    staticClass: "TX"
  }, [_vm._v("Looking back on a career of more than 40 years in clinical neurology and psychiatry and my involvement in experimental neuroscience, I realize that the struggles for recognition of the discipline of neuropsychiatry are still with us. In this book, my contribution, I explore some of the reasons and the intellectual contests involved. Certain disorders are relevant to the developing background of today’s practice of neuropsychiatry. These include especially epilepsy, syphilis, hysteria, movement disorders, dementia, psychosis, and melancholia. But such a portrayal cannot ignore interesting ideas that held sway in Europe throughout much of the nineteenth century, such as the rise and fall of monomania and the degeneration theory.")])]), _c('p', {
    staticClass: "TX"
  }, [_c('a', {
    attrs: {
      "id": "pg_ix"
    }
  }, [_vm._v("Neuropsychiatry is concerned not only with descriptions of clinical abnormalities that relate to our understanding of brain-behavior relationships but also with the meaning of abnormal behavior. This requires consideration of content as well as form and the various life contingencies that impinge on patients, which may influence their symptoms. This also calls for consideration of the huge gulf between disease (pathology) and illness (what patients present with) and a propensity to tolerate uncertainty. William Alwyn ")]), _c('a', {
    attrs: {
      "id": "pg_x"
    }
  }, [_vm._v("Lishman, in his paper “What Is Neuropsychiatry?,” explained that neuropsychiatry was not an “all-exclusive domain” embracing only the neurosciences, but that “social, developmental, psychodynamic and interpersonal forces must also be considered.” He warns, “One must take issue with those who claim that the neuropsychiatric approach can account for all mental disorder. Such vaulting ambition is reminiscent of the once proud claims of psychodynamic theory.”"), _c('span', {
    staticClass: "en-ref"
  })]), _c('a', {
    attrs: {
      "href": "#/xhtml/#pref_1",
      "id": "pref-1"
    }
  }, [_vm._v("1")])]), _vm._v(" "), _c('p', {
    staticClass: "TX"
  }, [_vm._v("Neuropsychiatry is not simply an offshoot of psychiatry. It is a discipline that has arisen out of a clinical need for patients who have fallen badly between the cracks engendered by the developments of the clinical neurosciences in the twentieth century. Every psychiatric disorder is accompanied by abnormal movements; movement disorders such as Parkinson’s disease invariably have associated mental state changes, and Gilles de la Tourette syndrome is not simply a tic disorder. Likewise, epilepsy is not just about seizures. It is a diagnosis of a continuous interruption of cerebral electrochemical activity in the brain with intermittent eruptions of such excessive activity that a clinical seizure is observed. If the first question of the treating physician is “How many fits have you had?” then the physician needs to review his or her understanding of the disorder. Living with a fear of the next seizure, the social limitations and stigma of the diagnosis, and the need in many cases for continuous medications harbors much more than the “fit” itself. Even the word "), _c('span', {
    staticClass: "ePub-I"
  }, [_vm._v("seizure")]), _vm._v(" has considerable historical and social baggage attached to it.")]), _vm._v(" "), _c('p', {
    staticClass: "TX"
  }, [_vm._v("Such euphemisms abound in clinical neurological practice; in some cases they dance around terms such as "), _c('span', {
    staticClass: "ePub-I"
  }, [_vm._v("functional")]), _vm._v(", "), _c('span', {
    staticClass: "ePub-I"
  }, [_vm._v("supratentorial")]), _vm._v(", "), _c('span', {
    staticClass: "ePub-I"
  }, [_vm._v("non-neurological")]), _vm._v(", "), _c('span', {
    staticClass: "ePub-I"
  }, [_vm._v("medically unexplained")]), _vm._v(", and the like, avoiding the well-trodden, always reemerging "), _c('span', {
    staticClass: "ePub-I"
  }, [_vm._v("hysteria")]), _vm._v(", with implications of exaggeration of complaints if not frank malingering. Dementia in its various guises has become the feared scourge of the twenty-first century. Barely of interest to an earlier generation of neurologists, who left it to “geriatric” psychiatrists to pick up the pieces, it most obviously requires a neuropsychiatric approach to understanding and management.")]), _vm._v(" "), _c('p', {
    staticClass: "TX"
  }, [_vm._v("The narrative of this book echoes the development of Western civilization, from the early city-states of the Greeks ("), _c('span', {
    staticClass: "ePub-I"
  }, [_vm._v("civitas")]), _vm._v(") to the intellectual and artistic achievements that remain part of our contemporary culture. Two great figures whose ideas reverberate through this text are Aristotle and Plato. The mathematician and philosopher Alfred North Whitehead said that all Western philosophy was a footnote to Plato, yet it was Aristotle who thought "), _c('a', {
    attrs: {
      "id": "pg_xi"
    }
  }, [_vm._v("that the philosopher should end with medicine and the physician commence with philosophy."), _c('span', {
    staticClass: "en-ref"
  })]), _c('a', {
    attrs: {
      "href": "#/xhtml/#pref_2",
      "id": "pref-2"
    }
  }, [_vm._v("2")]), _vm._v(" According to the historian Arthur Herman, the idea that the rise of modern science involved a struggle between reason and religion is not merely wrong but also misleading."), _c('span', {
    staticClass: "en-ref"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#pref_3",
      "id": "pref-3"
    }
  }, [_vm._v("3")])]), _vm._v(" It is true that the overwhelming pressures of the monotheistic religions on scientific advancement have been profound, yet the real struggle has been between Aristotle and Plato. Both were concerned with things eternal and immutable, but they also needed to assimilate into their ideas what was transitory and changing. As the cultural philosopher George Steiner phrased it, “Only, moreover, in classical Greece and its European legacy, is the theoretical applied to the practical in the guise of a universal critique of all life and of its goals. There is a sharp distinction to be drawn between this phenomenology and the ‘mythicopractical’ fabric of Far Eastern or Indian models. The seminal fact of wonder, "), _c('span', {
    staticClass: "ePub-I"
  }, [_vm._v("thaumazein")]), _vm._v(", and of theoretical-logical development is Platonic and Aristotelian to the core.”"), _c('span', {
    staticClass: "en-ref"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#pref_4",
      "id": "pref-4"
    }
  }, [_vm._v("4")])]), _vm._v(" For Plato the idealist, wisdom came not through the senses, but through contemplation, reason, and wonder, while for Aristotle, knowledge was derived a posteriori, from experience.")]), _vm._v(" "), _c('p', {
    staticClass: "TX"
  }, [_vm._v("The earliest intimation of what today we call neuropsychiatry begins with Hippocrates, who laid the foundations of our clinical approach to medicine. His affirmations that the brain is the seat of emotions, as well as epilepsy and diseases of the mind, began a trail of thought and experiment leading to our current attempts to understand mind–brain relationships. Some other civilizations, such as the Egyptians and Babylonians, left documentary evidence that disorders we still identify were a part of their medical experience, including descriptions of what we now call mental or psychiatric disorders, but they left no hints as to what they thought might lie behind such illnesses, and nothing of what they thought about the brain and its functions.")]), _vm._v(" "), _c('p', {
    staticClass: "TX"
  }, [_vm._v("I seek in this book to explore ideas over the past 2,500 years which have contributed to the intellectual legacy of what is now referred to as neuropsychiatry. The writings and speculations of our predecessors, without the tools we now have for investigation and a variety of well-honed treatments, are worthy of consideration.")]), _vm._v(" "), _c('p', {
    staticClass: "TX"
  }, [_vm._v("The empirical view of history, preferred by the Anglo-Saxon tradition, would be based firmly on facts, seeking natural “laws” that are discoverable. Yet there has been the alternative view of history, as a venture seeking not so much knowledge but understanding, linked especially to the European schools of hermeneutics, or text interpretation. Meaning in these paradigms precedes knowledge, the historian taking what scattered pieces of documentation or "), _c('a', {
    attrs: {
      "id": "pg_xii"
    }
  }, [_vm._v("artifacts are available and working them into a tapestry of identifiable ideas, however incomplete the picture. The historian-philosopher Wilhelm Dilthey realized that there were no natural “laws” for history, as in the physical sciences, and drew distinctions between the latter ("), _c('span', {
    staticClass: "ePub-I"
  }, [_vm._v("Naturwissenschaften")]), _vm._v(") and the human sciences ("), _c('span', {
    staticClass: "ePub-I"
  }, [_vm._v("Geisteswissenschaften")]), _vm._v("), discussed more in later chapters. While the natural sciences seek explanation by way of cause and effect, in contrast, the human sciences explore “understanding”: the relations of parts to the whole and to what might be referred to as the human spirit, understanding as a manifestation of lived experience ("), _c('span', {
    staticClass: "ePub-I"
  }, [_vm._v("Erlebnis")]), _vm._v("). The German "), _c('span', {
    staticClass: "ePub-I"
  }, [_vm._v("verstehen")]), _vm._v(" (“to understand”) applied to several areas of inquiry, including historical texts, law, philosophy, and, as we see in ")]), _c('span', {
    attrs: {
      "href": "#/xhtml/chapter14",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("chapter 14")]), _vm._v(", through Karl Jaspers, psychiatry. Romantic in orientation and concerned with language as the basis for the construction of all human narratives, including science, this approach acknowledged that historical knowledge was not acquired in the same way as scientific knowledge, and that general “laws” of history are very hard to come by. As the historian Jacob Burckhardt noted, “History is the record of what one age finds worthy of note in another.”"), _c('span', {
    staticClass: "en-ref"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#pref_5",
      "id": "pref-5"
    }
  }, [_vm._v("5")])]), _vm._v(" What emerges as having historical significance in this account is more than tinged with the writer’s personal indulgence, for which I can only apologize.")]), _vm._v(" "), _c('p', {
    staticClass: "TX"
  }, [_vm._v("One perspective on history, which is hard to shift, is that its trajectory is like the arrow of Odysseus, traveling with precision in only one direction through 12 narrow axes; time’s arrow is always straight and the aim teleological. History in this view is not only a progressive science but also a science of progress. But there are many turning points in history; the question is which turn to take and how to negotiate the chosen path. In science Thomas Kuhn’s "), _c('span', {
    staticClass: "ePub-I"
  }, [_vm._v("The Structure of Scientific Revolutions")]), _vm._v(" (1962) shattered the idea of scientific discovery being one of continuous progress, based on nineteenth-century positivism, implying that paradigm shifts occur at times when anomalies in the data reach such a point that the theories they portend to support become untenable. The intellectual consensus breaks down, new models emerge that are incompatible with the older ones, and so it goes on. A key example that has to be acknowledged in the history of psychiatry is the rapid transition from the psychoanalytic emphasis of the first part of the twentieth century to the biological paradigms of the later decades.")]), _vm._v(" "), _c('p', {
    staticClass: "TX"
  }, [_vm._v("This dramatic shift of the underlying basis for the discipline has a historical backdrop, which has been discussed many times by both the physicians who lived through the changes and the professional historians, often with quite competing visions of the different interests involved and their "), _c('a', {
    attrs: {
      "id": "pg_xiii"
    }
  }, [_vm._v("cultural and social environments. I am not a professional historian but a researcher and practitioner with considerable interest and curiosity to understand from whence my discipline came and how we got to where we are today. This book is not intended to document a linear trajectory, nor one with a teleological directive; indeed, it is not a history in any traditional sense. History belongs for me, as Nietzsche described it, “to him who preserves and reveres—to him who looks back to whence he has come, to where he came into being, with love and loyalty; with this piety he as it were gives thanks for his existence.”"), _c('span', {
    staticClass: "en-ref"
  })]), _c('a', {
    attrs: {
      "href": "#/xhtml/#pref_6",
      "id": "pref-6"
    }
  }, [_vm._v("6")])]), _vm._v(" "), _c('p', {
    staticClass: "TX"
  }, [_vm._v("There will undoubtedly be those who would criticize this approach. Indeed, this book is not intended to be a historical exegesis as might be written by a professional historian. If there is criticism over my use of terms such as "), _c('span', {
    staticClass: "ePub-I"
  }, [_vm._v("romanticism")]), _vm._v(" or "), _c('span', {
    staticClass: "ePub-I"
  }, [_vm._v("Enlightenment")]), _vm._v(" for various historical periods, this is well accepted, since all isms and epochs retrospectively allocated are bound to be controversial. The chapter headings and divisions are interlinked with certain historically bound ideas, and I hope they serve to highlight trends that relate not only to neuroscience but also to the arts and culture of the times. I make no apology for linking neuroscience perspectives to poetry, literature, or developments of musical style. Literature is an essential pathway to feeling the human situation and examining an individual’s responses to the environment, with the contingencies of everyday life of those who succeed and those who suffer. If it was Samuel Taylor Coleridge who in England opened up a discussion of the creative brain and introduced us to the word "), _c('span', {
    staticClass: "ePub-I"
  }, [_vm._v("unconscious")]), _vm._v(" (1818), it was Jane Austen who around the same time explored the inner motives of her characters, and Johann Wolfgang von Goethe who gave us both Werther and Faust.")]), _vm._v(" "), _c('p', {
    staticClass: "TX"
  }, [_vm._v("Many explorers of the brain discussed in this book, such as Franz Joseph Gall, Erasmus Darwin, John Hughlings Jackson, Sigmund Freud, and Sir Charles Sherrington, considered philosophy central to their enterprise. They were interested in minds as well as brains, and they searched for new insights within the confines of the times and the ideas prevalent when they were alive.")]), _vm._v(" "), _c('p', {
    staticClass: "TX"
  }, [_vm._v("There are some who proclaim that there is no conceptual core or stable definition of neuropsychiatry. German Berrios in his historical overview of the subject describes the “elusive nature of the social practice of neuropsychiatry [as] the most important obstacle for writing its history.” Indeed, this is a perspective in which psychiatry itself “appears to be just another discipline created by society to perform certain anthropological, managerial "), _c('a', {
    attrs: {
      "id": "pg_xiv"
    }
  }, [_vm._v("and policing duties.”"), _c('span', {
    staticClass: "en-ref"
  })]), _c('a', {
    attrs: {
      "href": "#/xhtml/#pref_7",
      "id": "pref-7"
    }
  }, [_vm._v("7")]), _vm._v(" But surely there is a history to be explored in one way or another?")]), _vm._v(" "), _c('p', {
    staticClass: "TX"
  }, [_vm._v("There may be difficulties in defining neuropsychiatry and finding the right words to counterbalance such criticisms. As will become clear, neuropsychiatry as explored in this book has veritable origins, but it is not until "), _c('span', {
    attrs: {
      "href": "#/xhtml/chapter10",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("chapter 10")]), _vm._v(" that the recognizable discipline of today materializes. In the late nineteenth century, psychopathologists were neuropathologists and vice versa, and their interest was with the brain and its abnormalities in those with certain behavior disorders. There was, however, a clear conceptual and practical schism by the late nineteenth century, widening in the first half of the twentieth century, such that a brain-based neurology and a psychological psychiatry that avoided flirting with neuroscience were the eventual outcomes. Over this time there were practitioners, especially in Europe, who were forerunners of today’s neuropsychiatrists, but they perhaps were viewed as neither neurologists nor psychiatrists, but akin to a mythological chimera.")]), _vm._v(" "), _c('p', {
    staticClass: "TX"
  }, [_vm._v("The term "), _c('span', {
    staticClass: "ePub-I"
  }, [_vm._v("neuropsychiatry")]), _vm._v(" entered the English language in 1918, some 70 years after the word "), _c('span', {
    staticClass: "ePub-I"
  }, [_vm._v("psychiatry")]), _vm._v(" (1847). Shortly thereafter emerged the neuropsychiatrist (1922). The situation has now altered considerably. There has been a growing tradition in the twentieth century for books to be published with “Neuropsychiatry” in the title. Stanley Cobb’s "), _c('span', {
    staticClass: "ePub-I"
  }, [_vm._v("Foundations of Neuropsychiatry")]), _vm._v(" was first published in 1936. There was a series titled "), _c('span', {
    staticClass: "ePub-I"
  }, [_vm._v("Recent Advances in Neurology and Neuropsychiatry")]), _vm._v(" which began in 1946 and ran for several years under the editorship of Lord Brain. Derek Richter’s "), _c('span', {
    staticClass: "ePub-I"
  }, [_vm._v("Perspectives in Neuropsychiatry")]), _vm._v(" was published in 1950. He became director of the Medical Research Council’s Neuropsychiatric Research Unit in Carshalton, England, in 1960.")]), _vm._v(" "), _c('p', {
    staticClass: "TX"
  }, [_vm._v("The modern era of clinical neuropsychiatric texts perhaps began around the 1980s. My own "), _c('span', {
    staticClass: "ePub-I"
  }, [_vm._v("Neuropsychiatry")]), _vm._v(" was published in 1981, and Jeff Cummings’s "), _c('span', {
    staticClass: "ePub-I"
  }, [_vm._v("Clinical Neuropsychiatry")]), _vm._v(" in 1985."), _c('span', {
    staticClass: "en-ref"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#pref_8",
      "id": "pref-8"
    }
  }, [_vm._v("8")])]), _vm._v(" On the bookshelves in front of me are a dozen books with “Neuropsychiatry” emblazoned on the spines. The British Neuropsychiatric Association was established in 1987, the American Neuropsychiatric Association in 1988, and the Japanese Neuropsychiatric Association in 1996. The International Neuropsychiatric Association (INA) was formed in 1998—neuropsychiatry is now a well-recognized discipline in many countries.")]), _vm._v(" "), _c('p', {
    staticClass: "TX"
  }, [_vm._v("There are several suggested definitions. Those who would narrowly limit neuropsychiatry simply with a claim that its flagship is “mental disorders are disorders of the brain” miss the wider implications of the subject and "), _c('a', {
    attrs: {
      "id": "pg_xv"
    }
  }, [_vm._v("conflate it with an allied but separate enterprise, namely, behavioral neurology."), _c('span', {
    staticClass: "en-ref"
  })]), _c('a', {
    attrs: {
      "href": "#/xhtml/#pref_9",
      "id": "pref-9"
    }
  }, [_vm._v("9")]), _vm._v(" As explored further in "), _c('span', {
    attrs: {
      "href": "#/xhtml/chapter13",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("chapter 13")]), _vm._v(", behavioral neurology arose from a subspecialty of neurology, the neurology of behavior, which was most interested in correlating focal structural lesions with behavioral changes. Behavioral neurology received its imprimatur with the publication in 1965 of Norman Geschwind’s paper “Disconnexion Syndromes in Animals and Man,” and the term is generally attributed to him. Behavioral neurology was particularly concerned with aphasia and similar disorders—related, yes, but quite different in approach and practice from neuropsychiatry.")]), _vm._v(" "), _c('p', {
    staticClass: "TX"
  }, [_vm._v("The INA came up with the following definition, admittedly a compromise: “Neuropsychiatry is a field of scientific medicine that concerns itself with the complex relationship between human behaviour and brain function, and endeavours to understand abnormal behaviour and behavioural disorders on the basis of an interaction of neurobiological and psychological–social factors. It is rooted in clinical neuroscience and provides a bridge between the disciplines of Psychiatry, Neurology and Neuropsychology.”"), _c('span', {
    staticClass: "en-ref"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#pref_10",
      "id": "pref-10"
    }
  }, [_vm._v("10")])])]), _vm._v(" "), _c('p', {
    staticClass: "TX"
  }, [_vm._v("In my original book "), _c('span', {
    staticClass: "ePub-I"
  }, [_vm._v("Neuropsychiatry")]), _vm._v(", I had ventured the following: neuropsychiatry is a discipline that references certain disorders that, “on account of their presentation and pathogenesis, do not fall neatly into one category, and require multidisciplinary ideas for their full understanding.”"), _c('span', {
    staticClass: "en-ref"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#pref_11",
      "id": "pref-11"
    }
  }, [_vm._v("11")])]), _vm._v(" Obviously, the definition bears heavily but not exclusively on what at the time was conventional neurology and psychiatry, covering a spectrum of disorders from epilepsy to conversion hysteria.")]), _vm._v(" "), _c('p', {
    staticClass: "TX"
  }, [_vm._v("Cummings in 1994 offered the following: “Neuropsychiatry is a clinical discipline devoted to understanding the neurobiological basis, optimal assessment, natural history, and most efficacious treatment of disorders of the nervous system with behavioural manifestations.”"), _c('span', {
    staticClass: "en-ref"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#pref_12",
      "id": "pref-12"
    }
  }, [_vm._v("12")])]), _vm._v(" He emphasized that neuropsychiatry “did not challenge the viability of psychiatry.… Neuropsychiatry, like neurology, is concerned with disorders of brain function and views behavioural abnormalities from a neurobiological perspective … based on neuroanatomy and neurophysiology and attempts to understand the mechanisms of behaviour, whereas psychiatry integrates information from psychology, sociology and anthropology to grasp the motivation of behaviour.”"), _c('span', {
    staticClass: "en-ref"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#pref_13",
      "id": "pref-13"
    }
  }, [_vm._v("13")])])]), _vm._v(" "), _c('p', {
    staticClass: "TX"
  }, [_vm._v("Indeed, the waters and the waves between psychiatry and neuropsychiatry are considerable; the latter is not simply “the predominant incarnation of psychiatry.”"), _c('span', {
    staticClass: "en-ref"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#pref_14",
      "id": "pref-14"
    }
  }, [_vm._v("14")])]), _vm._v(" It may well be that there are many psychiatrists who would label themselves as neuropsychiatrists, to single themselves out as having a "), _c('a', {
    attrs: {
      "id": "pg_xvi"
    }
  }, [_vm._v("special expertise, yet they might lack not only the fundamental training for such a moniker but also the historical feel for the special nature of the discipline they are claiming. Perhaps this book will fill in some of the gaps. However, without an intimate acquaintance in particular with neuroanatomy and neuropsychology, simply struggling with the names of various neuroreceptors and transmitters and knowing enough psychopharmacology to adequately prescribe psychotropic agents do not add up to being a neuropsychiatrist.")])]), _c('a', {
    attrs: {
      "id": "pg_xvi"
    }
  }), _c('p', {
    staticClass: "TX"
  }, [_c('a', {
    attrs: {
      "id": "pg_xvi"
    }
  }, [_vm._v("The book opens with a reminder of the transmission by our early ancestors of nature into culture, of raw meat into the cooked, and the importance of storytelling to "), _c('span', {
    staticClass: "ePub-I"
  }, [_vm._v("Homo sapiens.")]), _vm._v(" Early chapters look at the Greek, Roman, and Arabic legacy but reflect only poverty of intellectual inquiry with regard to the brain. Interest in its ventricles held pride of place, but for some such as Aristotle the heart was considered to be the prime mover. ")]), _c('a', {
    attrs: {
      "href": "#/xhtml/chapter2"
    }
  }, [_vm._v("Chapter 2")]), _vm._v(" explores the so-called Dark Ages, which were not at all dark culturally, but in which religious strictures bound any rigorous exploration of the mind. Anatomical dissection was either forbidden or confined to animals: the soul was God’s business.")]), _vm._v(" "), _c('p', {
    staticClass: "TX"
  }, [_vm._v("The word "), _c('span', {
    staticClass: "ePub-I"
  }, [_vm._v("anatomy")]), _vm._v(" entered the English lexicon in the early sixteenth century. Andreas Vesalius performed anatomical explorations of the human brain, Thomas Willis took the brain out of the skull, and René Descartes kept the mind business away from the body, giving scientists a freedom to explore the brain. Although Descartes’s work opened a way to examine reflex function, his separation of mind and body has held generations of intellectuals in thrall ever since.")]), _vm._v(" "), _c('p', {
    staticClass: "TX"
  }, [_vm._v("The story moves forward at a more rapid pace through the time periods addressed in "), _c('span', {
    attrs: {
      "href": "#/xhtml/chapter3",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("chapters 3")]), _vm._v(" and "), _c('span', {
    attrs: {
      "href": "#/xhtml/chapter4",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("4")]), _vm._v(", when the foundations of a neuroscience were apparent. Martin Luther shook the shackles of the Catholic Church by asserting the value of individual freedom from authority, the kernel of the romantic revolution that is a central theme of this book. A neurologically based understanding of the individual’s expressive self was hardly entertained by the philosopher-scientists of the so-called Enlightenment era. Then there was a shift from a passive view of the brain’s engagement with the world, simply as a receiver of sensations, to an active one. The liberation of the individual came with romanticism, but with an anti-Enlightenment Platonic bias.")]), _vm._v(" "), _c('p', {
    staticClass: "TX"
  }, [_vm._v("Alan Richardson noted, “Most work on the Romantic mind continues to be informed by a disembodied version of associationism, by psychoanalysis, "), _c('a', {
    attrs: {
      "id": "pg_xvii"
    }
  }, [_vm._v("or by epistemological issues that link Romantic literary figures to a philosophical tradition running from German idealism to phenomenology and its deconstruction. The Romantic brain, however, has been left almost wholly out of account.”"), _c('span', {
    staticClass: "en-ref"
  })]), _c('a', {
    attrs: {
      "href": "#/xhtml/#pref_15",
      "id": "pref-15"
    }
  }, [_vm._v("15")]), _vm._v(" This book is an attempt to address this lacuna, and I hope it will be of interest to any neurothinker who might wonder how the prominent split between neurology and psychiatry came about.")]), _vm._v(" "), _c('p', {
    staticClass: "TX"
  }, [_c('span', {
    attrs: {
      "href": "#/xhtml/chapter5",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("Chapters 5")]), _vm._v(" and "), _c('span', {
    attrs: {
      "href": "#/xhtml/chapter6",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("6")]), _vm._v(" introduce some neuroscientists and their ideas, which now may seem only shadows extinguished by the light of progress, yet they embraced a broad understanding of human nature and were interested in a romantic vision. Some were poets (such as Erasmus Darwin) or artists (such as Charles Bell), but they emphasized links between life and nature, seeking harmony between parts and a holistic view of how the brain functioned. It was the nature of unity and the unity of nature that were paramount.")]), _vm._v(" "), _c('p', {
    staticClass: "TX"
  }, [_vm._v("The story continues in "), _c('span', {
    attrs: {
      "href": "#/xhtml/chapter7",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("chapters 7")]), _vm._v(" and "), _c('span', {
    attrs: {
      "href": "#/xhtml/chapter8",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("8")]), _vm._v(", with travels around Europe. Paris became the center of activity in the nineteenth century, and then the focus shifted to Germany, with English contributions being represented by Hughlings Jackson, who may be considered the father of modern neuropsychiatry.")]), _vm._v(" "), _c('p', {
    staticClass: "TX"
  }, [_vm._v("It is in "), _c('span', {
    attrs: {
      "href": "#/xhtml/chapter9",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("chapters 9")]), _vm._v(" and "), _c('span', {
    attrs: {
      "href": "#/xhtml/chapter10",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("10")]), _vm._v(" that the major developments splitting a newly developed discipline of neurology from the psychologically based psychiatry are outlined. There were some, however, who refused to give up on what became today’s neuropsychiatry, as discussed in "), _c('span', {
    attrs: {
      "href": "#/xhtml/chapter11",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("chapters 11")]), _vm._v(" and "), _c('span', {
    attrs: {
      "href": "#/xhtml/chapter12",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("12")]), _vm._v(". This involved not only the discovery of the power of a seizure to dramatically alter the course of some mental disorders but also the discovery of the electroencephalogram (EEG). Epilepsy became the meeting point of neurology and psychiatry, bolstered by new discoveries in neuroanatomy and neurochemistry ("), _c('span', {
    attrs: {
      "href": "#/xhtml/chapter13",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("chapter 13")]), _vm._v(").")]), _vm._v(" "), _c('p', {
    staticClass: "TX"
  }, [_vm._v("In many ways the definitions of neuropsychiatry given above do not conjure up the most important aspect of the rebirth of neuropsychiatry, namely, that the discipline arose out of a clinical need. There were so many patients whose problems were at the interface between the developed, encapsulated, segregated fields of neurology and psychiatry who were just badly served by the conceptual and administrative gulf that arose. The enterprise was catalyzed by the growing understanding of neuroanatomy, neuropsychology, and neurophysiology; the use of newer methods of investigation of the brain, especially the EEG and later brain imaging; and a growing awareness that a clinical understanding of the signs and symptoms of central nervous system (CNS) dysfunction could not be embraced by localizationalist neurological "), _c('a', {
    attrs: {
      "id": "pg_xviii"
    }
  }, [_vm._v("theories. Doctrines of psychoanalysis and behaviorist approaches likewise fell on fallow ground. Neuropsychiatry implies that its practitioners—neuropsychiatrists—are very familiar not only with the above-cited clinical skills and methods of investigation but also with the signs and symptoms of a range of CNS disorders and the psychology of human motivation and desire. What is it that makes us tick, and when does the tic become pathological?")])]), _c('a', {
    attrs: {
      "id": "pg_xviii"
    }
  }, [_c('p', {
    staticClass: "TX"
  }, [_vm._v("Throughout the book, certain terms have become interchangeable. "), _c('span', {
    staticClass: "ePub-I"
  }, [_vm._v("Mental illness")]), _vm._v(", "), _c('span', {
    staticClass: "ePub-I"
  }, [_vm._v("madness")]), _vm._v(", "), _c('span', {
    staticClass: "ePub-I"
  }, [_vm._v("insanity")]), _vm._v(", and the like are often used, relating to the words that were current in the times of the quoted authors. However, they are also used quite loosely, likely to irritate anyone who, harboring a potent Aristotelian tradition, believes that committee-produced sets of diagnostic criteria should be adhered to since they explain the essence of clinical syndromes.")]), _vm._v(" "), _c('p', {
    staticClass: "TX"
  }, [_vm._v("Clinicians generally, but certainly those with an interest in the neurosciences, can be crudely divided into the “classical” and the “romantic.” The former tend to concern themselves more with parts than wholes, reduction rather than richness, Aristotle rather than Plato. Their preference is for life’s experiences to be reduced to mathematical formulas and rating scale instruments, using wire and box diagrams to explain how the brain works. The subtleties of careful clinical explorations of patients’ histories, signs, and symptoms come after laboratory testing and brain imaging. Yet any neuroscience understanding of human experiences, either pathological or within the realms of all of us, must take account of those that might be labeled as mystical or transcendental, or even trans-ascendental. Those interested in neuropsychiatry cannot ignore such phenomena.")]), _vm._v(" "), _c('p', {
    staticClass: "TX"
  }, [_vm._v("The Aristotelian perspective is never far behind, and the tensions between the philosophies of Aristotle and Plato reveal discords that themselves are not easily harmonized. To leave Plato out of things does not seem possible.")]), _vm._v(" "), _c('p', {
    staticClass: "ENH"
  }, [_c('span', {
    staticClass: "ePub-SC"
  }, [_vm._v("NOTES")])])]), _c('p', {
    staticClass: "NTX"
  }, [_c('a', {
    attrs: {
      "id": "pg_xviii"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#pref-1",
      "id": "pref_1"
    }
  }, [_vm._v("1")]), _vm._v(".  Lishman WA, 1992, 984.")]), _vm._v(" "), _c('p', {
    staticClass: "NTX"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#pref-2",
      "id": "pref_2"
    }
  }, [_vm._v("2")]), _vm._v(".  Donley JE, 1904, 1–11.")]), _vm._v(" "), _c('p', {
    staticClass: "NTX"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#pref-3",
      "id": "pref_3"
    }
  }, [_vm._v("3")]), _vm._v(".  Herman A, 2013, 327.")]), _vm._v(" "), _c('p', {
    staticClass: "NTX"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#pref-4",
      "id": "pref_4"
    }
  }, [_vm._v("4")]), _vm._v(".  Steiner G, 2015, 56.")]), _vm._v(" "), _c('p', {
    staticClass: "NTX"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#pref-5",
      "id": "pref_5"
    }
  }, [_vm._v("5")]), _vm._v(".  Burckhardt J, 1929, quoted in Carr A, 1964, 27.")]), _vm._v(" "), _c('p', {
    staticClass: "NTX"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#pref-6",
      "id": "pref_6"
    }
  }, [_vm._v("6")]), _vm._v(".  Nietzsche F, 1873, 1876/1997, 72.")]), _vm._v(" "), _c('p', {
    staticClass: "NTX"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#pref-7",
      "id": "pref_7"
    }
  }, [_vm._v("7")]), _vm._v(".  See Berrios GE, Markova, IS, 2002, 629, 630.")]), _vm._v(" "), _c('p', {
    staticClass: "NTX"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#pref-8",
      "id": "pref_8"
    }
  }, [_vm._v("8")]), _vm._v(".  Richter D, 1950; Trimble MR, 1981; Cummings J, 1985.")]), _vm._v(" "), _c('p', {
    staticClass: "NTX"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#pref-9",
      "id": "pref_9"
    }
  }, [_vm._v("9")]), _vm._v(".  Berrios GE, Markova IS, 2002, 629. On behavioral neurology, see Benson F, 1993.")]), _vm._v(" "), _c('p', {
    staticClass: "NTX"
  }, [_c('a', {
    attrs: {
      "id": "pg_xix"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#pref-10",
      "id": "pref_10"
    }
  }, [_vm._v("10")]), _vm._v(". Sachdev P, 2005, 191.")]), _vm._v(" "), _c('p', {
    staticClass: "NTX"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#pref-11",
      "id": "pref_11"
    }
  }, [_vm._v("11")]), _vm._v(".  Trimble MR, 1981, xiv.")]), _vm._v(" "), _c('p', {
    staticClass: "NTX"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#pref-12",
      "id": "pref_12"
    }
  }, [_vm._v("12")]), _vm._v(".  Cummings J, Hegarty A, 1994, 209.")]), _vm._v(" "), _c('p', {
    staticClass: "NTX"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#pref-13",
      "id": "pref_13"
    }
  }, [_vm._v("13")]), _vm._v(".  Cummings J, Hegarty A, 211.")]), _vm._v(" "), _c('p', {
    staticClass: "NTX"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#pref-14",
      "id": "pref_14"
    }
  }, [_vm._v("14")]), _vm._v(".  Berrios GE, Markova, IS, 2002, 636.")]), _vm._v(" "), _c('p', {
    staticClass: "NTX"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#pref-15",
      "id": "pref_15"
    }
  }, [_vm._v("15")]), _vm._v(".  Richardson A, 2001, 1.")]), _vm._v(" "), _c('p', {
    staticClass: "ENH"
  }, [_c('span', {
    staticClass: "ePub-SC"
  }, [_vm._v("REFERENCES")])]), _vm._v(" "), _c('p', {
    staticClass: "BIB"
  }, [_vm._v("Benson F. The history of behavioural neurology. "), _c('span', {
    staticClass: "ePub-I"
  }, [_vm._v("Behavioural Neurology")]), _vm._v(" 1993;11:1–8.")]), _vm._v(" "), _c('p', {
    staticClass: "BIB"
  }, [_vm._v("Berrios GE, Markova IS. The concept of neuropsychiatry: A historical overview. "), _c('span', {
    staticClass: "ePub-I"
  }, [_vm._v("Journal of Psychosomatic Research")]), _vm._v(" 2002;53:629–638.")]), _vm._v(" "), _c('p', {
    staticClass: "BIB"
  }, [_vm._v("Carr EH. "), _c('span', {
    staticClass: "ePub-I"
  }, [_vm._v("What Is History?")]), _vm._v(" Penguin Books, London; 1964.")]), _vm._v(" "), _c('p', {
    staticClass: "BIB"
  }, [_vm._v("Cummings J. "), _c('span', {
    staticClass: "ePub-I"
  }, [_vm._v("Clinical Neuropsychiatry")]), _vm._v(". Grune and Stratton, New York; 1985.")]), _vm._v(" "), _c('p', {
    staticClass: "BIB"
  }, [_vm._v("Cummings J, Hegarty A. Neurology, psychiatry, and neuropsychiatry. "), _c('span', {
    staticClass: "ePub-I"
  }, [_vm._v("Neurology")]), _vm._v(" 1994;44:209–213.")]), _vm._v(" "), _c('p', {
    staticClass: "BIB"
  }, [_vm._v("Donley JE. On the early history of cerebral localization. "), _c('span', {
    staticClass: "ePub-I"
  }, [_vm._v("American Journal of the Medical Sciences")]), _vm._v(" October 1904;1–11.")]), _vm._v(" "), _c('p', {
    staticClass: "BIB"
  }, [_vm._v("Herman A. "), _c('span', {
    staticClass: "ePub-I"
  }, [_vm._v("The Cave and the Light: Plato versus Aristotle, and the Struggle for the Soul of Western Civilisation")]), _vm._v(". Random House, London; 2013.")]), _vm._v(" "), _c('p', {
    staticClass: "BIB"
  }, [_vm._v("Kuhn TS. "), _c('span', {
    staticClass: "ePub-I"
  }, [_vm._v("The Structure of Scientific Revolutions")]), _vm._v(". University of Chicago Press, Chicago; 1962.")]), _vm._v(" "), _c('p', {
    staticClass: "BIB"
  }, [_vm._v("Lishman WA. What is neuropsychiatry? "), _c('span', {
    staticClass: "ePub-I"
  }, [_vm._v("Journal of Neurology, Neurosurgery and Neuropsychiatry")]), _vm._v(" 1992;55:983–985.")]), _vm._v(" "), _c('p', {
    staticClass: "BIB"
  }, [_vm._v("Nietzsche F. On the uses and disadvantages of the history of life. In: Breazeale D, ed. "), _c('span', {
    staticClass: "ePub-I"
  }, [_vm._v("Untimely Meditations")]), _vm._v(". Cambridge University Press, Cambridge; 1873, 1876/1997.")]), _vm._v(" "), _c('p', {
    staticClass: "BIB"
  }, [_vm._v("Richardson A. "), _c('span', {
    staticClass: "ePub-I"
  }, [_vm._v("British Romanticism and the Science of the Mind")]), _vm._v(". Cambridge University Press, Cambridge; 2001.")]), _vm._v(" "), _c('p', {
    staticClass: "BIB"
  }, [_vm._v("Richter D. "), _c('span', {
    staticClass: "ePub-I"
  }, [_vm._v("Perspectives in Neuropsychiatry")]), _vm._v(". H. K. Lewis, London; 1950.")]), _vm._v(" "), _c('p', {
    staticClass: "BIB"
  }, [_vm._v("Sachdev P. International Neuropsychiatric Association. "), _c('span', {
    staticClass: "ePub-I"
  }, [_vm._v("Neuropsychiatric Disease and Treatment")]), _vm._v(" 2005;1:191–192.")]), _vm._v(" "), _c('p', {
    staticClass: "BIB"
  }, [_vm._v("Steiner G. "), _c('span', {
    staticClass: "ePub-I"
  }, [_vm._v("The Idea of Europe: An Essay")]), _vm._v(". Overlook Press, London; 2015.")]), _vm._v(" "), _c('p', {
    staticClass: "BIB"
  }, [_vm._v("Trimble MR. "), _c('span', {
    staticClass: "ePub-I"
  }, [_vm._v("Neuropsychiatry")]), _vm._v(". John Wiley and Sons, Chichester; 1981.")]), _vm._v(" "), _c('p', {
    staticClass: "BIB"
  }, [_vm._v("Woolf V. "), _c('span', {
    staticClass: "ePub-I"
  }, [_vm._v("Mrs Dalloway")]), _vm._v(". Penguin, London; 1925/1996."), _c('a', {
    attrs: {
      "id": "pg_xx"
    }
  })]), _c('a', {
    attrs: {
      "id": "pg_xx"
    }
  })])])
}]}


//////////////////
// WEBPACK FOOTER
// ../node_modules/vue-loader/lib/template-compiler?{"id":"data-v-11292466","hasScoped":false,"transformToRequire":{"video":"src","source":"src","img":"src","image":"xlink:href"}}!../node_modules/vue-loader/lib/selector.js?type=template&index=0!./src/components/xhtml/preface.vue
// module id = 78
// module chunks = 0